import clsx from "clsx";

import { useDesignElementManager } from "@hooks/use-design-element-manager";

import {
  LEGACY_PLATFORMS,
  type LegacyPlatform,
} from "../../utms/legacy-platforms";
import { Platform } from "../constants";

import { SetupSection } from "./components/setup-section";
import { Code, OrderedList, SubSubSectionHeader } from "./components/styled";
import { URLVerification, UtmParamsClipboard } from "./components/utms";
import { VideoContent } from "./components/video-content";
import { type PlatformDetails } from "./platform-details";

const PLATFORM = Platform.META;

export const MetaDetails: PlatformDetails = {
  northbeamDocUrl:
    "https://docs.northbeam.io/docs/setting-up-facebook-ads-tracking",
  officialDocUrl: "",
  additionalDetails: [
    {
      title: "Meta Ads includes the following platforms:",
      content: (
        <ul className="mb-0 ml-2 space-y-1 list-disc list-inside marker:text-xxs">
          <li>Facebook</li>
          <li>WhatsApp</li>
          <li>Messenger</li>
          <li>Instagram</li>
        </ul>
      ),
    },
    {
      title: "What does the integration do?",
      /* eslint-disable max-len */
      content:
        "Northbeam natively integrates with the Facebook Ads API. By doing so, we pull all your current and historical Campaigns, Adsets, and Ads from your account, along with a list of useful metrics. Notably, we pull impressions, which fuel our view model and unlock our Clicks + Modeled Views attribution model. We also pull ad spend and list of in-platform conversion & creative metrics to help you with decision making.",
    },
    {
      title: "How do Meta UTMs work?",
      content: (
        <ul className="mb-0 ml-2 space-y-1 list-disc list-inside marker:text-xxs">
          <li>
            Connecting your Meta account will not modify any of your existing
            ads or revert them to the learning phase.
          </li>
          <li>
            Please contact Northbeam customer success if you want to disable
            auto-appending UTMs for Facebook Ads (or any other platform).
          </li>
        </ul>
      ),
    },
    {
      title: "What about Meta shops?",
      content:
        "For Northbeam to attribute Meta shops orders, they must be present and tagged in your orders table.",
    },
  ],
};

const LEGACY_PLATFORM_CONTENT: { [key in LegacyPlatform]: React.ReactNode } = {
  hyros: (
    <>
      <h3 className="text-lg font-semibold leading-normal">Required values:</h3>
      <Code className="w-fit">fbc_id=$ADSET_ID&h_ad_id=$AD_ID</Code>
    </>
  ),
  "triple-whale": (
    <>
      <h3 className="text-lg font-semibold leading-normal">
        One of the following source parameters + values is required:
      </h3>
      <OrderedList variant="disc" className="ml-2">
        <>
          tw_source = <Code>{"{{ site_source_name }}"}</Code>
        </>
        <>
          tw_source = <Code>facebook</Code>
        </>
        <>
          utm_source = <Code>facebook</Code>
        </>
        <>
          utm_source = <Code>{"{{ site_source_name }}"}</Code>
        </>
      </OrderedList>
      <h3 className="text-lg font-semibold leading-normal">
        AND one of the following parameters + values:
      </h3>
      <OrderedList variant="disc" className="ml-2">
        <>
          tw_adid = <Code>{"{{ ad.id }}"}</Code>
        </>
        <>
          fbadid = <Code>{"{{ ad.id }}"}</Code>
        </>
      </OrderedList>
    </>
  ),
  rockerbox: (
    <>
      <h3 className="text-lg font-semibold leading-normal">Required:</h3>
      <Code className="w-fit">
        fb_campaignid=$CAMPAIGN_ID&fb_adsetid=$ADSET_ID&fb_adid$AD_ID
      </Code>
    </>
  ),
  wickedreports: (
    <>
      <h3 className="text-lg font-semibold leading-normal">Required:</h3>
      <Code className="w-fit">WickedSource=Facebook&WickedID=$AD_ID</Code>
    </>
  ),
};

export const MetaSetup = ({
  connectionContent,
  legacyPlatforms = [],
}: {
  connectionContent: React.ReactNode;
  legacyPlatforms?: LegacyPlatform[];
}) => {
  const { designElementManager } = useDesignElementManager();
  return (
    <>
      <SetupSection title="Log into Meta Ads to connect integration">
        {connectionContent}
      </SetupSection>
      <SetupSection title="Follow the UTM instructions in the video below">
        <div className="space-y-4">
          <VideoContent
            title="Learn how to structure Meta UTMs"
            platform={PLATFORM}
            videoUrl="https://www.loom.com/embed/dfd6bbb850b2426eaaf8b74315986575"
          />
          <div className="space-y-2">
            <SubSubSectionHeader>
              Make sure you’ve followed these steps:
            </SubSubSectionHeader>
            <div className="flex gap-2">
              <span>2a.</span>
              <span>You will need to add UTM parameters to all</span>
              <Code>destination URLs</Code>
              <span>for new and existing ads</span>
            </div>
          </div>
          <span>
            <b>Note:</b> Changing the tracking parameters for an existing ad
            will reset it to the learning phase. We recommend waiting for a
            low-traffic period to do so. You may choose to roll out UTM updates
            progressively, or you may “rip off the bandaid” and update all
            Facebook ads at once. If you decide to roll out updates
            progressively, please be aware that our reporting will be inaccurate
            until all updates are complete.
          </span>
          <span>
            <b>Note:</b> Northbeam will attempt to place UTM parameters on all
            newly created Facebook ads, but you will need to update your
            historic ads
          </span>
        </div>
      </SetupSection>
      <SetupSection title="Copy these UTM parameters into Meta Ads">
        <UtmParamsClipboard
          platform={PLATFORM}
          className={clsx("max-w-2xl", legacyPlatforms.length > 0 && "pb-10")}
          aggregateMultipleParams={false}
        />
        {legacyPlatforms.length > 0 && (
          <div className="flex flex-col gap-4 text-sm">
            <h1 className="mx-0 mt-0 mb-0 text-lg font-semibold leading-normal heading">
              OR use legacy UTMs for historic ads
            </h1>
            <span>
              <b>Note:</b> Legacy UTMs prevent historic ads from resetting to
              “learning phase”. For the better attribution, we prefer that you
              use Northbeam’s UTM.
            </span>
            <span>
              <b>Note:</b> If you have multiple legacy attribution tools for
              this platform, you can only use one legacy UTM for each historic
              ad.
            </span>
            {legacyPlatforms.map((platform) => (
              <div
                key={platform}
                className="flex flex-col gap-4 rounded-lg border"
              >
                <div className="flex gap-2 items-center p-4 border-b">
                  <div className="inline-flex">
                    {designElementManager
                      .getDesignElement(platform)
                      .getComponent()}
                    <div className="-ml-2">
                      {designElementManager
                        .getDesignElement("facebook")
                        .getComponent()}
                    </div>
                  </div>
                  <h1 className="mx-0 mt-0 mb-0 text-lg font-semibold leading-normal heading">
                    {LEGACY_PLATFORMS.getLabel(platform)}, Legacy Facebook UTMs
                  </h1>
                </div>
                <div className="flex flex-col gap-4 px-4 pb-6">
                  {LEGACY_PLATFORM_CONTENT[platform]}
                </div>
              </div>
            ))}
          </div>
        )}
      </SetupSection>
      <URLVerification platform={PLATFORM} />
    </>
  );
};
